/**
 * DesignerNodeShell — Shared card shell for all designer nodes
 *
 * Encapsulates the ~90% shared UI pattern:
 * - Border color state machine (selected > running > hovered > default)
 * - Image area 4-state rendering (empty, running-no-image, running-blur, displayed)
 * - Loading shimmer
 * - Error footer (2-line clamp + click-to-expand)
 * - Fullscreen lightbox (portal to document.body)
 * - Handle rendering (invisible 2x2px, from HandleConfig[])
 * - AgentNodeToolbar integration (Variants conditional, Duplicate, Delete)
 *
 * Per-designer content (name/desc area, quick info bar, bottom panel)
 * is provided via the `children` slot.
 *
 * Sprint 3a: Designer Nodes — Shared Foundation
 */

import React, { memo, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Handle, useReactFlow } from 'reactflow'
import { BookmarkPlus, Check, Loader2, Maximize2, Palette } from 'lucide-react'
import { getAssetUrl, getCanvasPreviewUrl } from '../../../../api/useAssetUrl'
import { AnnotationEditorOverlay, useImageAnnotation } from './annotate'
import { useCanvasEdges } from '../../CanvasEdgesContext'
import { useHandlesVisibleForNode } from '../../HandleVisibilityContext'
import { useCanvasNodeChromeMode } from '../../CanvasNodeChromeModeContext'
import { useSetHoveredNodeId } from '../../NodeHoverContext'
import { useBlockingOverlay } from '../../../../hooks/canvasShortcutGuard'
import { AgentNodeToolbar, useNodeActions, useSaveToLibrary, type ToolbarAction } from './'
import { useDuplicateMenuItems } from './useDuplicateMenuItems'
import { AddToChatIcon } from './AddToChatIcon'
import { computeBorderStyle } from './designerUtils'
import { FlatIdleNodeCard } from './FlatIdleNodeCard'
import { NODE_BG_SWATCH_COLORS, resolveNodeBgColor, localizeNodeBgColor } from './nodeBgColors'
import type { HandleConfig } from './types'
import type { AgentColorConfig } from '../../../../constants/agentColors'
import { cn } from '../../../../lib/utils'
import { NODE_CARD_RADIUS_CLASS, NODE_CARD_INNER_RADIUS_CLASS } from './constants'
import type { NodeStatus } from '../../../../generated/nodeDataTypes'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { createDebug, createNadDebug } from '../../../../utils/debugLog'
import { useCanvasNodeImage } from './useCanvasNodeImage'
import { resolveIdentityBorderState, useNodeIdentityBorderTrace } from './useNodeIdentityBorderTrace'
import { buildAssetDownloadFilename } from '../../../../utils/buildDownloadFilename'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'
import { NodeStatusMessage } from './NodeStatusMessage'
import { MetaLoadingSurface, type MetaLoadingDescriptor } from './MetaLoadingSurface'
import type { VideoHistoryEntry } from './variantOrdinals'
import { useRegisterNodeContextAction } from '../../contextMenu/nodeContextActions'

const sizeLog = createDebug('dw:size:designer-shell')
// Feature: node-card-radius — trace breadcrumb confirming the standardised
// outer node-card corner radius token is applied (RFC: standardize-canvas-node-corner-radius).
// `dw:` prefix so createDebug.enable('dw:*') picks it up and `nad trace --feature
// node-card-radius` matches (see utils/debugLog.ts _parseNamespace).
const radiusLog = createNadDebug('dw:node-card-radius')
// Feature: node-bg-color-border — trace breadcrumb confirming the idle/hover
// border derives from the user's custom node color (in_bg_color) instead of the
// agent identity palette. `dw:` prefix so `nad trace --feature
// node-bg-color-border` matches (see utils/debugLog.ts _parseNamespace).
const bgColorBorderLog = createNadDebug('dw:node-bg-color-border')

export interface DesignerNodeShellProps {
  /** ReactFlow node ID */
  nodeId: string
  /** Whether the node is selected */
  selected: boolean | undefined
  /** Whether the node is being dragged */
  dragging: boolean | undefined
  /** Node status */
  status: NodeStatus
  /** Error message if status === 'error' */
  error?: string
  /** Progress message during running state */
  progressMessage?: string
  /** Node name for "Creating {name}..." message */
  nodeName?: string
  /** Agent type label (e.g., "Character", "Location") for running/empty messages */
  agentTypeLabel: string
  /** ReactFlow node type (e.g., "characterDesigner"), recorded in the identity-border trace */
  nodeType: string
  /** Agent color config from AGENT_COLORS */
  agentColor: AgentColorConfig
  /** Card width in px */
  width: number
  /** CSS class for image aspect ratio (e.g., 'aspect-video', 'aspect-[4/3]') */
  imageAspect: string
  /** Handle definitions */
  handles: HandleConfig[]
  /** data-testid for the card container */
  testId?: string
  /** Resolved image path (from resolveDesignerImagePath) */
  imagePath?: string
  /** Cache key for URL cache busting */
  cacheKey?: string
  /** Empty state icon (SVG path d attribute) */
  emptyIconPath: string
  /** Size class for the empty icon (e.g., 'w-8 h-8', 'w-10 h-10') */
  emptyIconSize?: string
  /** Per-designer content area (name/desc, quick info bar, etc.) */
  children: React.ReactNode
  /** Bottom panel rendered inside the relative wrapper but outside the card (avoids overflow-hidden clipping) */
  bottomPanel?: React.ReactNode
  /** Floating label rendered above the card (e.g., node name) */
  floatingLabel?: React.ReactNode
  /** Callback to stop a running execution */
  onCancel?: () => void
  /** Whether stop request is in progress */
  isCancelling?: boolean
  /** Image variant history (for v<NN> suffix in download filename). Optional. */
  imageHistory?: VideoHistoryEntry[]
  /** Picked variant index inside imageHistory. Defaults to 0 when imageHistory is provided without one. */
  pickedIndex?: number
  /** Current background color key (e.g. 'purple', 'teal') */
  bgColorKey?: string
  /** Callback when user picks a background color */
  onBgColorChange?: (colorKey: string) => void
}

const DesignerNodeShell = memo(function DesignerNodeShell({
  nodeId,
  selected,
  dragging,
  status,
  error,
  progressMessage,
  nodeName,
  agentTypeLabel,
  nodeType,
  agentColor,
  width,
  imageAspect,
  handles,
  testId,
  imagePath,
  cacheKey,
  emptyIconPath,
  emptyIconSize = 'w-8 h-8',
  children,
  bottomPanel,
  floatingLabel,
  onCancel,
  isCancelling,
  imageHistory,
  pickedIndex,
  bgColorKey,
  onBgColorChange,
}: DesignerNodeShellProps) {
  const nodePresentationWidth = useNodePresentationWidth()
  const { t } = useTranslation('canvas')
  const projectId = useProjectId()
  const { handleDelete, handleDuplicate, handleDownload, handleAddToChat } = useNodeActions({ nodeId })
  const duplicateMenuItems = useDuplicateMenuItems(handleDuplicate, nodeName || agentTypeLabel)
  const { saveState, handleSaveToLibrary, saveLabel } = useSaveToLibrary({ nodeId, selected })
  const { getNodes } = useReactFlow()
  const edges = useCanvasEdges()
  const handlesVisible = useHandlesVisibleForNode(nodeId)
  const { lightweightNodeChrome } = useCanvasNodeChromeMode()
  const setHoveredNodeId = useSetHoveredNodeId()
  const [isHovered, setIsHovered] = useState(false)
  const [isHoveringLightweightNodeChrome, setIsHoveringLightweightNodeChrome] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  // Suspend canvas keyboard shortcuts while this overlay is open (see canvasShortcutGuard).
  useBlockingOverlay(isModalOpen)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const colorPickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!showColorPicker) return
    const handleClickOutside = (e: MouseEvent) => {
      if (colorPickerRef.current && e.target instanceof Node && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(false)
      }
    }
    document.addEventListener('pointerdown', handleClickOutside)
    return () => document.removeEventListener('pointerdown', handleClickOutside)
  }, [showColorPicker])

  useEffect(() => {
    if (!selected) setShowColorPicker(false)
  }, [selected])

  const isRunning = status === 'running'
  const isPendingExecution = status === 'pending_execution'
  const isPendingCost = status === 'pending_cost_confirmation'
  const showSpinner = isRunning || isPendingExecution || isPendingCost
  const loadingMessage = isRunning
    ? (progressMessage || (imagePath
      ? t('designerNodeShell.refining', { label: agentTypeLabel.toLowerCase() })
      : nodeName
        ? t('designerNodeShell.creatingNamed', { name: nodeName })
        : t('designerNodeShell.creating', { label: agentTypeLabel.toLowerCase() })))
    : isPendingCost
      ? t('designerNodeShell.waitingCostApproval')
      : t('designerNodeShell.queuedWaiting')
  const metaLoading = useMemo<MetaLoadingDescriptor | undefined>(() => {
    if (!showSpinner) return undefined
    return {
      message: loadingMessage,
      nodeType,
      loadingState: isPendingCost
        ? 'pending-cost'
        : isPendingExecution
          ? 'pending-execution'
          : 'running',
    }
  }, [isPendingCost, isPendingExecution, loadingMessage, nodeType, showSpinner])
  const cancelButton = isRunning && onCancel ? (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        onCancel()
      }}
      disabled={isCancelling}
      className="nodrag mt-3 flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-white/70 transition-colors hover:bg-white/20 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50"
      data-testid="cancel-node-button"
      title={t('designerNodeShell.stop')}
    >
      <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="currentColor">
        <rect x="6" y="6" width="12" height="12" rx="1" />
      </svg>
      <span className="text-xxs font-medium">{t('designerNodeShell.stop')}</span>
    </button>
  ) : null

  // Diagnostic logging for rendered node width
  sizeLog('[size] DesignerNodeShell: nodeId=%s width=%d', nodeId, width)
  radiusLog({ event: 'designer-card-radius', nodeId, cardRadiusClass: NODE_CARD_RADIUS_CLASS })

  // Image URL resolution
  const imageUrl =
    imagePath && projectId ? getCanvasPreviewUrl(projectId, imagePath, cacheKey) : null
  const fullImageUrl =
    imagePath && projectId ? getAssetUrl(projectId, imagePath, 0) : null

  // Border color state machine — custom node color (bgColorKey) drives the
  // idle/hover border so it matches the chosen fill instead of the agent palette.
  const borderStyle = computeBorderStyle(!!selected, status, isHovered, agentColor, bgColorKey)

  // Card side of the shared identity-border trace (post 1579940090429955), so
  // `nad trace --feature node-identity-border` shows this card next to the
  // variant picker for the same node_id. Distinct from the legacy
  // node-bg-color-border event below, which traces the custom-swatch branch.
  useNodeIdentityBorderTrace({
    nodeId,
    nodeType,
    surface: 'card',
    agentColor,
    appliedBorderColor: borderStyle.borderColor,
    borderState: resolveIdentityBorderState(!!selected, status, isHovered),
  })

  // Feature trace: emit only when an input to the border decision changes (not
  // every render), so `nad trace --feature node-bg-color-border` stays readable.
  useEffect(() => {
    // Mirror computeBorderStyle's precedence: error/selected/running/pending
    // short-circuit to the agent palette before bgColorKey is ever consulted.
    const usesCustom =
      !!bgColorKey &&
      bgColorKey !== 'default' &&
      status !== 'error' &&
      status !== 'running' &&
      status !== 'pending_execution' &&
      status !== 'pending_cost_confirmation' &&
      !selected
    bgColorBorderLog({
      event: 'designer-border-resolved',
      nodeId,
      bgColorKey: bgColorKey ?? 'default',
      selected: !!selected,
      status,
      isHovered,
      branch: usesCustom ? 'custom' : 'agent-default',
      resolvedBorderColor: borderStyle.borderColor,
    })
  }, [nodeId, bgColorKey, selected, status, isHovered, borderStyle.borderColor])

  // Download handler — standardized filename via shared helper.
  const onDownload = useCallback(() => {
    if (!imagePath) return
    const { filename } = buildAssetDownloadFilename({
      canvasNodes: getNodes(),
      canvasEdges: edges,
      nodeId,
      assetPath: imagePath,
      mediaType: 'image',
      variantHistory: imageHistory,
      pickedIndex,
    })
    handleDownload(imagePath, filename)
  }, [imagePath, handleDownload, getNodes, edges, nodeId, imageHistory, pickedIndex])
  useRegisterNodeContextAction(nodeId, 'download', onDownload, Boolean(imagePath))

  // RFC-1115: `nodeType` is the frontend node type, which is what resolves

  // the image output port from the generated schemas.

  const annotate = useImageAnnotation({

    nodeId,

    feNodeType: nodeType,

    imagePath,

    imageUrl: fullImageUrl,

    projectId,

  })


  // Promote only the active image node, and keep open overlays mounted.
  // Use the same predicate for the queue and the rendered branch so flat idle
  // media never reserves a full-card loader slot that it cannot release.
  const shouldRenderFlatNode = lightweightNodeChrome && !selected && !dragging &&
    !isHoveringLightweightNodeChrome && !isModalOpen && !annotate.isOpen
  const shouldRenderNodeChrome = !shouldRenderFlatNode
  const previewRef = useRef<HTMLDivElement | null>(null)
  const { displayUrl: previewUrl, onMediaSettled } = useCanvasNodeImage(
    imageUrl,
    previewRef,
    { enabled: !shouldRenderFlatNode },
  )

  // Toolbar actions
  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      ...(onBgColorChange ? [{
        key: 'bg-color',
        label: t('designerNodeShell.backgroundColor'),
        icon: <Palette size={14} />,
        onClick: () => setShowColorPicker((prev) => !prev),
      }] : []),
      {
        key: 'addToChat',
        label: t('designerNodeShell.addToChat'),
        icon: <AddToChatIcon size={14} />,
        onClick: handleAddToChat,
        group: !!onBgColorChange,
        nuxAnchorId: 'node-toolbar-add-to-chat',
        nuxControlId: 'addToChat',
      },
      {
        key: 'save-to-library',
        label: saveLabel,
        icon:
          saveState === 'loading' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : saveState === 'success' ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <BookmarkPlus className="w-4 h-4" />
          ),
        onClick: handleSaveToLibrary,
        disabled: saveState !== 'idle',
        group: true,
      },
      {
        ...annotate.toolbarAction,
        nuxAnchorId: nodeType === 'characterDesigner' ? 'character-visual-tools' : undefined,
        nuxControlId: nodeType === 'characterDesigner' ? 'annotate' : undefined,
      },
      {
        key: 'duplicate',
        label: t('designerNodeShell.duplicate'),
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        menuItems: duplicateMenuItems,
        group: true,
      },
      {
        key: 'delete',
        label: t('designerNodeShell.delete'),
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [duplicateMenuItems, handleDelete, handleAddToChat, handleSaveToLibrary, saveState, saveLabel, onBgColorChange, t, annotate.toolbarAction, nodeType]
  )

  if (shouldRenderFlatNode) {
    return (
      <div
        data-testid="designer-node-lightweight-host"
        className="w-fit"
        onPointerEnter={() => { setIsHoveringLightweightNodeChrome(true); setIsHovered(true); setHoveredNodeId(nodeId) }}
        onPointerLeave={() => { setIsHoveringLightweightNodeChrome(false); setIsHovered(false); setHoveredNodeId(null) }}
      >
        <FlatIdleNodeCard
          nodeId={nodeId}
          testId={testId ? `${testId}-flat` : 'designer-node-flat'}
          label={nodeName}
          fallbackLabel={agentTypeLabel}
          width={width}
          status={status}
          error={error ? sanitizeNodeError(error, t, true) : undefined}
          progressMessage={loadingMessage}
          metaLoading={metaLoading}
          handles={handles}
          mediaKind={imageUrl ? 'image' : 'none'}
          mediaUrl={imageUrl}
          outputVariantIndex={pickedIndex}
          aspectClassName={imageAspect}
          // Match the full card's `object-contain` so the preview doesn't change
          // framing as the canvas crosses the lightweight-render zoom threshold.
          mediaFit="contain"
          placeholderLabel={t('designerNodeShell.noLabelYet', { label: agentTypeLabel.toLowerCase() })}
          compactHandles
          backgroundColor={resolveNodeBgColor(bgColorKey)}
        />
      </div>
    )
  }

  return (
    <>
      <div
        className="relative"
        onPointerEnter={lightweightNodeChrome ? () => { setIsHoveringLightweightNodeChrome(true); setIsHovered(true); setHoveredNodeId(nodeId) } : undefined}
        onPointerLeave={lightweightNodeChrome ? () => { setIsHoveringLightweightNodeChrome(false); setIsHovered(false); setHoveredNodeId(null) } : undefined}
      >
        <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

        {/* RFC-1115 annotation editor. Mounted at the NODE ROOT, not inside the
            image container: that container clips (`overflow: hidden`), which cuts
            off the tool palette — wider than the node card — and leaves its
            buttons unclickable behind the ReactFlow pane. */}
        {annotate.isOpen && fullImageUrl && (
          <AnnotationEditorOverlay
            imageUrl={fullImageUrl}
            altText={agentTypeLabel}
            traceId={annotate.traceId}
            nodeId={nodeId}
            isSaving={annotate.isSaving}
            onCancel={annotate.close}
            onSave={annotate.save}
          />
        )}

        {/* Floating label above the card */}
        {floatingLabel}

        {/* Color picker panel */}
        {showColorPicker && onBgColorChange && (
          <div
            ref={colorPickerRef}
            data-testid="designer-color-picker"
            style={{
              position: 'absolute',
              top: -52,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 60,
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-1.5 rounded-full bg-surface-2 backdrop-blur-md border border-border shadow-xl px-3 py-2">
              {Object.entries(NODE_BG_SWATCH_COLORS).map(([key, swatchColor]) => {
                const isActive = (bgColorKey ?? 'default') === key
                return (
                  <button
                    key={key}
                    aria-label={localizeNodeBgColor(key, t)}
                    title={localizeNodeBgColor(key, t)}
                    onClick={(e) => {
                      e.stopPropagation()
                      onBgColorChange(key)
                      setShowColorPicker(false)
                    }}
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: swatchColor,
                      border: isActive ? '2px solid white' : '2px solid transparent',
                      cursor: 'pointer',
                      flexShrink: 0,
                      outline: 'none',
                      boxShadow: isActive ? '0 0 0 1px rgba(255,255,255,0.3)' : 'none',
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                    }}
                  />
                )
              })}
            </div>
          </div>
        )}

        {/* Main Card */}
        <div
          data-testid={testId}
          data-nux-anchor="node-visible"
          data-nux-node-id={nodeId}
          data-nux-node-type={nodeType}
          data-nux-node-status={status}
          className={cn(
            'canvas-node-card-contained relative border transition-all duration-200 overflow-hidden cursor-pointer',
            NODE_CARD_RADIUS_CLASS,
            showSpinner && 'animate-node-shimmer'
          )}
          style={{ width: nodePresentationWidth(width), backgroundColor: resolveNodeBgColor(bgColorKey), ...borderStyle }}
          onMouseEnter={() => {
            setIsHovered(true)
            setHoveredNodeId(nodeId)
          }}
          onMouseLeave={() => {
            setIsHovered(false)
            setHoveredNodeId(null)
          }}
        >
          {/* ReactFlow Handles */}
          {handlesVisible && handles.map((handle) => (
            <Handle
              key={handle.id}
              type={handle.type}
              position={handle.position}
              id={handle.id}
              className="!bg-transparent !border-0 !w-2 !h-2"
              style={handle.style}
            />
          ))}

          {/* Image Area — 4-state rendering */}
          <div ref={previewRef} className="relative group p-1.5">
            {/* RFC-1115 annotation editor. `inset-1.5` matches the container's
                padding so the editable surface lines up with the image. */}

            {/* State 1: Image displayed (with optional running blur)

                `object-contain`, not `object-cover`: the preview box is a fixed
                `imageAspect` (16:9 for all three designers) but the render is
                only 16:9 if the provider can actually produce it. GPT Image 2
                has three physical output shapes and maps a 16:9 request to
                1536x1024 — 3:2 — so `object-cover` center-cropped ~15.6% of the
                height off every character sheet (T287457882). Contain letterboxes
                instead, and is a no-op for providers that do honor 16:9. The
                bars share the placeholder's `bg-surface-1` so the box doesn't
                change color when the asset lands. */}
            {previewUrl && (
              <img
                src={previewUrl}
                alt={agentTypeLabel}
                aria-hidden={showSpinner || undefined}
                decoding="async"
                className={cn(
                  `w-full ${imageAspect} object-contain bg-surface-1 block ${NODE_CARD_INNER_RADIUS_CLASS}`,
                  showSpinner && 'pointer-events-none'
                )}
                onLoad={onMediaSettled}
                onError={onMediaSettled}
              />
            )}

            {/* Queued behind the canvas image pool: hold the exact image box so
                the card never resizes when the asset lands. */}
            {imageUrl && !previewUrl && (
              <div
                aria-hidden="true"
                className={`w-full ${imageAspect} bg-surface-1 block ${NODE_CARD_INNER_RADIUS_CLASS}`}
              />
            )}

            {/* State 2: Running overlay with existing image */}
            {metaLoading && imageUrl && (
              <MetaLoadingSurface
                {...metaLoading}
                projectId={projectId}
                nodeId={nodeId}
                presentation="full"
                className={NODE_CARD_INNER_RADIUS_CLASS}
                style={{ inset: '0.375rem' }}
              >
                {cancelButton}
              </MetaLoadingSurface>
            )}

            {/* State 3: Running/pending without image */}
            {!imageUrl && metaLoading && (
              <div
                className={`relative w-full ${imageAspect} bg-surface-1/50 ${NODE_CARD_INNER_RADIUS_CLASS}`}
              >
                <MetaLoadingSurface
                  {...metaLoading}
                  projectId={projectId}
                  nodeId={nodeId}
                  presentation="full"
                  className={NODE_CARD_INNER_RADIUS_CLASS}
                >
                  {cancelButton}
                </MetaLoadingSurface>
              </div>
            )}

            {/* State 4: Empty */}
            {!imageUrl && !showSpinner && (
              <div
                className={`w-full ${imageAspect} bg-surface-1/50 ${NODE_CARD_INNER_RADIUS_CLASS} flex flex-col items-center justify-center`}
              >
                <svg
                  className={`${emptyIconSize} text-text-muted/20`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d={emptyIconPath}
                  />
                </svg>
                <span className="text-xxs text-text-muted/40 mt-1">
                  {t('designerNodeShell.noLabelYet', { label: agentTypeLabel.toLowerCase() })}
                </span>
              </div>
            )}

            {/* Expand / download buttons */}
            {imageUrl && !showSpinner && (
              <div className="absolute top-[14px] right-[14px] flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsModalOpen(true)
                  }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors nodrag"
                  title={t('designerNodeShell.expand')}
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onDownload()
                  }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors nodrag"
                  title={t('designerNodeShell.download')}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </button>
              </div>
            )}

            {/* Status overlay — floats over the bottom of the image box (inset
                to match the container padding) instead of adding a footer, so a
                failure never changes the card's outline size (post
                1634117708345526). Only while status is error, so stale error
                text doesn't linger after a successful re-execution. */}
            {status === 'error' && error && (
              <NodeStatusMessage
                message={sanitizeNodeError(error, t, true)}
                testId="designer-card-error"
                className="inset-x-1.5 bottom-1.5 rounded-b-sm"
              />
            )}
          </div>

          {/* Per-designer content area (name/desc, quick info bar) */}
          {shouldRenderNodeChrome && children}
        </div>

        {/* Bottom panel (outside card to avoid overflow-hidden clipping) */}
        {bottomPanel}
      </div>

      {/* Fullscreen Lightbox */}
      {isModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <img
                src={fullImageUrl ?? undefined}
                alt={t('designerNodeShell.altFullSize')}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
})

DesignerNodeShell.displayName = 'DesignerNodeShell'
export { DesignerNodeShell }
import { useNodePresentationWidth } from '../../NodePresentationContext'
